import { z } from "zod";
import {
  AR_STATEMENT_RECIPIENT_ROLE_MODES,
  SystemEvent,
} from "@erp/shared-types";
import { defineStepConfig } from "../../../registry/helpers";

/**
 * Scan Payment Reminder Customers
 *
 * Finds every customer with open invoices strictly older than
 * `invoiceAgeDays` (on the configured aging basis) who has NOT been sent a
 * statement / payment reminder within `reminderCooldownDays`, and queues them
 * all into a single AR task. The task's action payload opens the bulk
 * statement send dialog pre-populated with those customers so the user can
 * review and send every reminder in one pass.
 *
 * The task is a per-organization singleton: a re-scan refreshes the open
 * task's customer list in place, and a scan that comes back empty cancels a
 * still-pending task so the dashboard never shows stale work.
 */
export const scanPaymentReminderCustomersStep = defineStepConfig({
  type: "scan_payment_reminder_customers",
  name: "Scan Payment Reminder Customers",
  description:
    "Find customers with old open invoices who haven't received a payment " +
    "reminder recently, and queue them into a single bulk-statement-send task.",
  category: "Data Access",
  libraryModule: "AccountsReceivable",

  // No data inputs — just the implicit flow signal from the schedule trigger.
  inputSchema: z.object({}),

  outputSchema: z.object({
    taskIds: z
      .string()
      .array()
      .describe(
        "Ids of the tasks filed this run — one per configured assignee, or a " +
          "single unassigned task when none are configured. Empty when no work " +
          "was found.",
      ),
    taskCreated: z
      .boolean()
      .describe(
        "True when at least one new task was created this run; false when all " +
          "tasks were refreshed in place or no task was needed.",
      ),
    customerCount: z
      .number()
      .int()
      .nonnegative()
      .describe("Number of customers queued for a payment reminder."),
    totalOpenBalance: z
      .string()
      .describe(
        "Total open balance (decimal string) across the queued customers.",
      ),
  }),

  configSchema: z.object({
    invoiceAgeDays: z
      .number()
      .int()
      .positive()
      .default(21)
      .describe(
        "Include customers with open invoices STRICTLY older than this many " +
          "days on the configured aging basis.",
      ),
    reminderCooldownDays: z
      .number()
      .int()
      .positive()
      .default(7)
      .describe(
        "Skip customers who were already sent a statement within this many " +
          "days. Customers never sent one are always eligible.",
      ),
    contentMinInvoiceAgeDays: z
      .number()
      .int()
      .nonnegative()
      .default(0)
      .describe(
        "Invoices included in the reminder content must be at least this " +
          "many days old on the same aging basis. Independent of " +
          "invoiceAgeDays, which only decides who qualifies for a reminder. " +
          "Set to 0 to include ALL of a customer's outstanding invoices.",
      ),
    agingBasis: z
      .enum(["days_overdue", "days_since_invoice", "days_since_shipment"])
      .default("days_since_invoice")
      .describe(
        "How invoice age is measured: since due date (days_overdue), since " +
          "invoice date, or since ship date.",
      ),
    statementRecipientRoles: z
      .enum(AR_STATEMENT_RECIPIENT_ROLE_MODES)
      .optional()
      .describe(
        "How statement recipients are split across To and Cc for statements " +
          "opened from this task. first_to_rest_cc addresses the first " +
          "resolved contact on To and every other contact on Cc; to_all puts " +
          "every contact on To and leaves Cc empty. Leave unset to fall back " +
          "to the organization's AR statement recipient setting.",
      ),
    assigneeUserIds: z
      .uuid()
      .array()
      .optional()
      .describe(
        "Users to file the payment-reminder task for. One task is created per " +
          "user, each assigned to that person. Leave empty to create a single " +
          "unassigned task (it still shows on the AR summary dashboard).",
      ),
    taskPriority: z
      .enum(["low", "medium", "high", "urgent"])
      .default("medium")
      .describe("Priority of the generated task."),
  }),

  emits: [SystemEvent.TASK_CREATED] as const,

  execution: {
    startToCloseTimeout: "120 seconds",
    retry: {
      maximumAttempts: 3,
      backoffCoefficient: 2,
      initialInterval: "5 seconds",
    },
  },
});
